import { useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import FilterTrigger from "./FilterTrigger";
import type { FilterOption } from "../lib/productFilters";

// Indent per tree level (static class names so Tailwind keeps them).
const INDENT = ["pl-2", "pl-6", "pl-10", "pl-14", "pl-18"];

interface Props {
  // Noun shown on the trigger: "Product group".
  label: string;
  options: FilterOption[];
  // Option keys to keep. Empty = no filter, trigger reads "<label>: All".
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  // Search box above the list — for long lists (product groups), not short ones.
  searchable?: boolean;
}

// "Product group: All" style pick-to-include filter. Nothing checked means every row
// passes; checking narrows the matrix to those values. Selection lives in the
// page (session-only). Popover rather than DropdownMenu so a search box can
// sit inside without the menu's typeahead swallowing keystrokes.
export default function OptionFilter({
  label,
  options,
  selected,
  onChange,
  searchable = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? options.filter((o) => o.label.toLowerCase().includes(needle))
    : options;

  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  const summary =
    selected.size === 0
      ? "All"
      : selected.size === 1
        ? (options.find((o) => selected.has(o.key))?.label ?? "1 selected")
        : `${selected.size} selected`;

  const renderRow = (o: FilterOption) => {
    const checked = selected.has(o.key);
    return (
      <Button
        key={o.key}
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => toggle(o.key)}
        className={`h-auto w-full justify-start gap-2 py-1.5 pr-2 text-sm font-normal ${INDENT[Math.min(o.depth ?? 0, INDENT.length - 1)]}`}
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
          {checked && <Check className="h-4 w-4" />}
        </span>
        <span className="min-w-0 truncate">{o.label}</span>
      </Button>
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <FilterTrigger label={label} value={summary} />
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-64 flex-col gap-2 p-2">
        {searchable && (
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}`}
            aria-label={`Search ${label.toLowerCase()}`}
            className="h-8 text-xs"
          />
        )}
        {selected.size > 0 && (
          <div className="flex items-center justify-between px-2 text-xs text-muted-foreground">
            <span>{selected.size} selected</span>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() => onChange(new Set())}
            >
              Clear
            </Button>
          </div>
        )}
        <div className="max-h-72 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
          {visible.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">No matches.</p>
          ) : (
            visible.map(renderRow)
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
